/**
 * Home Page JavaScript
 *
 * Features:
 * - FAQ toggle (event delegation)
 * - Investment chart (lazy loaded with IntersectionObserver)
 *
 * CTA tracking is handled by analytics.html (SRP: centralized tracking)
 */

document.addEventListener('DOMContentLoaded', function() {
    // FAQ Toggle (Event Delegation - CSP Compliant)
    document.addEventListener('click', function(e) {
        const faqButton = e.target.closest('.faq-question');
        if (!faqButton) return;

        const faqItem = faqButton.parentElement;
        const isActive = faqItem.classList.contains('active');

        // Close all items
        document.querySelectorAll('.faq-item').forEach(item => {
            item.classList.remove('active');
        });

        // Open clicked item (if it wasn't active)
        if (!isActive) {
            faqItem.classList.add('active');
        }
    });

    // Lazy load Chart.js when investment chart is near viewport
    const chartCanvas = document.getElementById('investmentChart');
    if (chartCanvas) {
        const observer = new IntersectionObserver(
            function(entries) {
                if (entries[0].isIntersecting) {
                    observer.disconnect();
                    initInvestmentChart(chartCanvas);
                }
            },
            { rootMargin: '200px', threshold: 0 }
        );
        observer.observe(chartCanvas);
    }
});

/**
 * Initialize investment chart with lazy-loaded Chart.js
 * @param {HTMLCanvasElement} canvas - The canvas element
 */
async function initInvestmentChart(canvas) {
    console.log('📊 Loading Chart.js...');

    // Dynamic import - Chart.js only loads when needed
    const { Chart, LineController, LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend, Filler } = await import('chart.js');

    // Register components
    Chart.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend, Filler);

    console.log('✅ Chart.js loaded, initializing chart...');

    const ctx = canvas.getContext('2d');

    // Gradients
    const gradient1 = ctx.createLinearGradient(0, 0, 0, 400);
    gradient1.addColorStop(0, 'rgba(251, 191, 36, 0.25)');
    gradient1.addColorStop(1, 'rgba(251, 191, 36, 0.0)');

    const gradient2 = ctx.createLinearGradient(0, 0, 0, 400);
    gradient2.addColorStop(0, 'rgba(236, 72, 153, 0.25)');
    gradient2.addColorStop(1, 'rgba(236, 72, 153, 0.0)');

    const gradient3 = ctx.createLinearGradient(0, 0, 0, 400);
    gradient3.addColorStop(0, 'rgba(14, 165, 233, 0.25)');
    gradient3.addColorStop(1, 'rgba(14, 165, 233, 0.0)');

    const gradient4 = ctx.createLinearGradient(0, 0, 0, 400);
    gradient4.addColorStop(0, 'rgba(34, 197, 94, 0.25)');
    gradient4.addColorStop(1, 'rgba(34, 197, 94, 0.0)');

    // Theme colors
    const getChartColors = function(theme) {
        return {
            text: theme === 'dark' ? '#f5f5f5' : '#2d3748',
            title: theme === 'dark' ? '#f5f5f5' : '#1a202c',
            grid: theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
            tooltipBg: theme === 'dark' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(15, 23, 42, 0.98)',
            tooltipTitle: theme === 'dark' ? '#1a1a1a' : '#ffffff',
            tooltipBody: theme === 'dark' ? '#333333' : '#E2E8F0',
            tooltipBorder: theme === 'dark' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)'
        };
    };

    const initialTheme = document.documentElement.getAttribute('data-theme') || 'light';
    const chartColors = getChartColors(initialTheme);

    const chart = new Chart(ctx, {
        type: 'line',
        data: {
            labels: ['0', '12', '24', '36', '48'],
            datasets: [{
                label: '100% Permuta',
                data: [0, 5, 10, 46, 61],
                borderColor: '#FBBF24',
                backgroundColor: gradient1,
                tension: 0.4,
                pointRadius: 0,
                pointHoverRadius: 10,
                pointHoverBorderWidth: 3,
                pointHoverBackgroundColor: '#FBBF24',
                pointHoverBorderColor: '#fff',
                borderWidth: 3,
                fill: true,
                spanGaps: false,
                hoverBorderWidth: 5
            }, {
                label: 'SELIC',
                data: [0, 10, 21, 33, 46],
                borderColor: '#EC4899',
                backgroundColor: gradient2,
                tension: 0.4,
                pointRadius: 0,
                pointHoverRadius: 10,
                pointHoverBorderWidth: 3,
                pointHoverBackgroundColor: '#EC4899',
                pointHoverBorderColor: '#fff',
                borderWidth: 3,
                fill: true,
                spanGaps: false,
                hoverBorderWidth: 5
            }, {
                label: 'Aluguel Tradicional',
                data: [0, 9.5, 19.7, 30.7, 43],
                borderColor: '#0EA5E9',
                backgroundColor: gradient3,
                tension: 0.4,
                pointRadius: 0,
                pointHoverRadius: 10,
                pointHoverBorderWidth: 3,
                pointHoverBackgroundColor: '#0EA5E9',
                pointHoverBorderColor: '#fff',
                borderWidth: 3,
                fill: true,
                spanGaps: false,
                hoverBorderWidth: 5
            }, {
                label: 'Venda Imóvel À Vista',
                data: [-5, 5, 15, 26, 39],
                borderColor: '#22C55E',
                backgroundColor: gradient4,
                tension: 0.4,
                pointRadius: 0,
                pointHoverRadius: 10,
                pointHoverBorderWidth: 3,
                pointHoverBackgroundColor: '#22C55E',
                pointHoverBorderColor: '#fff',
                borderWidth: 3,
                fill: true,
                spanGaps: false,
                hoverBorderWidth: 5
            }]
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
                mode: 'index',
                intersect: false,
                axis: 'x'
            },
            onClick: function(event, activeElements, chart) {
                if (window.innerWidth < 768 && activeElements.length === 0) {
                    chart.tooltip.setActiveElements([], { x: 0, y: 0 });
                    chart.update();
                }
            },
            layout: {
                padding: { right: 80, top: 10 }
            },
            animation: {
                duration: 750,
                easing: 'easeInOutQuart'
            },
            scales: {
                x: {
                    title: {
                        display: true,
                        text: 'Meses',
                        font: {
                            size: window.innerWidth < 768 ? 12 : 14,
                            weight: '600',
                            family: 'Inter'
                        },
                        color: chartColors.title
                    },
                    ticks: {
                        color: chartColors.text,
                        font: {
                            size: window.innerWidth < 768 ? 10 : 12,
                            family: 'Inter'
                        },
                        padding: 8
                    },
                    grid: {
                        color: chartColors.grid,
                        drawBorder: false
                    }
                },
                y: {
                    ticks: {
                        callback: v => v + '%',
                        color: chartColors.text,
                        font: {
                            size: window.innerWidth < 768 ? 10 : 12,
                            family: 'Inter'
                        },
                        padding: 8
                    },
                    title: {
                        display: true,
                        text: 'Retorno (%)',
                        color: chartColors.title,
                        font: {
                            size: window.innerWidth < 768 ? 12 : 14,
                            weight: '600',
                            family: 'Inter'
                        }
                    },
                    grid: {
                        color: chartColors.grid,
                        drawBorder: false
                    }
                }
            },
            plugins: {
                legend: {
                    display: true,
                    position: 'bottom',
                    align: 'center',
                    labels: {
                        color: chartColors.text,
                        usePointStyle: true,
                        pointStyle: 'circle',
                        padding: window.innerWidth < 768 ? 12 : 20,
                        font: {
                            size: window.innerWidth < 768 ? 11 : 13,
                            weight: '500',
                            family: 'Inter'
                        },
                        boxWidth: window.innerWidth < 768 ? 6 : 8,
                        boxHeight: window.innerWidth < 768 ? 6 : 8
                    },
                    onClick: function(e, legendItem, legend) {
                        const index = legendItem.datasetIndex;
                        const chart = legend.chart;
                        const meta = chart.getDatasetMeta(index);
                        meta.hidden = !meta.hidden;
                        chart.update();
                    },
                    onHover: function(event, legendItem, legend) {
                        event.native.target.style.cursor = 'pointer';
                        const datasetIndex = legendItem.datasetIndex;
                        chart.data.datasets.forEach((dataset, index) => {
                            dataset.borderWidth = index === datasetIndex ? 5 : 2;
                        });
                        chart.update('none');
                    },
                    onLeave: function(event, legendItem, legend) {
                        event.native.target.style.cursor = 'default';
                        chart.data.datasets.forEach((dataset) => {
                            dataset.borderWidth = 3;
                        });
                        chart.update('none');
                    }
                },
                tooltip: {
                    enabled: true,
                    backgroundColor: chartColors.tooltipBg,
                    titleColor: chartColors.tooltipTitle,
                    bodyColor: chartColors.tooltipBody,
                    borderColor: chartColors.tooltipBorder,
                    borderWidth: 1,
                    padding: window.innerWidth < 768 ? 8 : 16,
                    displayColors: true,
                    boxWidth: window.innerWidth < 768 ? 8 : 12,
                    boxHeight: window.innerWidth < 768 ? 8 : 12,
                    boxPadding: window.innerWidth < 768 ? 4 : 6,
                    usePointStyle: true,
                    titleFont: {
                        size: window.innerWidth < 768 ? 12 : 15,
                        weight: '700',
                        family: 'Inter'
                    },
                    bodyFont: {
                        size: window.innerWidth < 768 ? 11 : 14,
                        weight: '500',
                        family: 'Inter'
                    },
                    bodySpacing: window.innerWidth < 768 ? 4 : 8,
                    cornerRadius: 8,
                    caretSize: window.innerWidth < 768 ? 6 : 8,
                    caretPadding: window.innerWidth < 768 ? 8 : 12,
                    maxWidth: window.innerWidth < 768 ? '85vw' : 'auto',
                    animation: { duration: 200 },
                    callbacks: {
                        title: function(context) {
                            return 'Mês ' + context[0].label;
                        },
                        label: function(context) {
                            const value = context.parsed.y;
                            const labelText = window.innerWidth < 768 ?
                                context.dataset.label.substring(0, 25) + (context.dataset.label.length > 25 ? '...' : '') :
                                context.dataset.label;
                            return ' ' + labelText + ': ' + value + '%';
                        },
                        afterBody: function(context) {
                            if (window.innerWidth >= 768 && context.length > 1) {
                                const values = context.map(c => c.parsed.y);
                                const max = Math.max(...values);
                                const maxIndex = values.indexOf(max);
                                return ['', '🏆 Melhor retorno: ' + context[maxIndex].dataset.label];
                            }
                            return '';
                        }
                    }
                }
            },
            onHover: function(event, activeElements) {
                event.native.target.style.cursor = activeElements.length > 0 ? 'pointer' : 'default';
            }
        },
        plugins: [{
            id: 'customLabels',
            afterDatasetsDraw: function(chart) {
                const ctx = chart.ctx;
                const labelPositions = [];
                const minDistance = 25;

                chart.data.datasets.forEach(function(dataset, datasetIndex) {
                    const meta = chart.getDatasetMeta(datasetIndex);
                    if (!meta.hidden) {
                        const lastPoint = meta.data[meta.data.length - 1];
                        const value = dataset.data[dataset.data.length - 1];
                        labelPositions.push({
                            datasetIndex: datasetIndex,
                            x: lastPoint.x,
                            y: lastPoint.y,
                            value: value,
                            color: dataset.borderColor,
                            originalY: lastPoint.y
                        });
                    }
                });

                labelPositions.sort((a, b) => a.y - b.y);

                for (let i = 1; i < labelPositions.length; i++) {
                    const current = labelPositions[i];
                    const previous = labelPositions[i - 1];
                    if (current.y - previous.y < minDistance) {
                        current.y = previous.y + minDistance;
                    }
                }

                labelPositions.forEach(function(label) {
                    const text = label.value + '%';
                    ctx.font = 'bold 13px Inter, sans-serif';
                    const textWidth = ctx.measureText(text).width;
                    const padding = 7;
                    const xPos = label.x + 12;
                    const yPos = label.y;
                    const boxWidth = textWidth + (padding * 2);
                    const boxHeight = 22;

                    // Connecting line
                    ctx.strokeStyle = label.color;
                    ctx.lineWidth = 2;
                    ctx.setLineDash([4, 4]);
                    ctx.beginPath();
                    ctx.moveTo(label.x, label.originalY);
                    ctx.lineTo(xPos - 2, yPos);
                    ctx.stroke();
                    ctx.setLineDash([]);

                    // Label box
                    ctx.shadowColor = 'rgba(0, 0, 0, 0.15)';
                    ctx.shadowBlur = 8;
                    ctx.shadowOffsetX = 0;
                    ctx.shadowOffsetY = 2;
                    ctx.fillStyle = label.color;
                    ctx.beginPath();
                    ctx.roundRect(xPos, yPos - (boxHeight / 2), boxWidth, boxHeight, 6);
                    ctx.fill();

                    // Reset shadow and draw text
                    ctx.shadowColor = 'transparent';
                    ctx.shadowBlur = 0;
                    ctx.fillStyle = '#ffffff';
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillText(text, xPos + (boxWidth / 2), yPos);
                });
            }
        }, {
            id: 'verticalCrosshair',
            afterDatasetsDraw: function(chart) {
                if (chart.tooltip && chart.tooltip._active && chart.tooltip._active.length) {
                    const activePoint = chart.tooltip._active[0];
                    const ctx = chart.ctx;
                    const x = activePoint.element.x;
                    const topY = chart.scales.y.top;
                    const bottomY = chart.scales.y.bottom;

                    ctx.save();
                    ctx.beginPath();
                    ctx.moveTo(x, topY);
                    ctx.lineTo(x, bottomY);
                    ctx.lineWidth = 2;
                    ctx.strokeStyle = 'rgba(59, 130, 246, 0.3)';
                    ctx.setLineDash([5, 5]);
                    ctx.stroke();
                    ctx.restore();

                    chart.tooltip._active.forEach(function(activeElement) {
                        const dataset = chart.data.datasets[activeElement.datasetIndex];
                        const point = activeElement.element;
                        ctx.save();
                        ctx.beginPath();
                        ctx.arc(point.x, point.y, 8, 0, 2 * Math.PI);
                        ctx.fillStyle = dataset.borderColor;
                        ctx.fill();
                        ctx.strokeStyle = '#fff';
                        ctx.lineWidth = 3;
                        ctx.stroke();
                        ctx.restore();
                    });
                }
            }
        }, {
            id: 'hoverAnimation',
            beforeEvent: function(chart, args) {
                if (args.event.type === 'mousemove') {
                    const activeElements = chart.getElementsAtEventForMode(args.event, 'index', { intersect: false }, false);
                    chart.data.datasets.forEach((dataset, index) => {
                        const isActive = activeElements.some(el => el.datasetIndex === index);
                        dataset.borderWidth = activeElements.length > 0 ? (isActive ? 5 : 2) : 3;
                    });
                }
            }
        }, {
            id: 'dataSource',
            beforeDatasetsDraw: function(chart) {
                const ctx = chart.ctx;
                const chartArea = chart.chartArea;
                const isMobile = window.innerWidth < 768;

                ctx.save();
                const fontSize1 = isMobile ? 9 : 11;
                const fontSize2 = isMobile ? 8 : 10;
                const lineHeight = isMobile ? 12 : 14;
                const padding = isMobile ? 4 : 6;

                ctx.textAlign = 'left';
                ctx.textBaseline = 'top';

                const line1 = isMobile ? 'Fonte: BCB, Secovi, ABRAINC' : 'Fonte: Banco Central do Brasil, Secovi, ABRAINC';
                const line2 = '*100% Permuta - Prazo médio de obras: 36 meses';

                const x = chartArea.left + (isMobile ? 5 : 10);
                const y = chartArea.top + (isMobile ? 5 : 10);

                ctx.font = `bold ${fontSize1}px Inter, sans-serif`;
                const width1 = ctx.measureText(line1).width;
                ctx.font = `italic ${fontSize2}px Inter, sans-serif`;
                const width2 = ctx.measureText(line2).width;

                const maxWidth = Math.max(width1, width2);
                const boxWidth = maxWidth + (padding * 2);
                const boxHeight = (lineHeight * 2) + (padding * 2);

                ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
                ctx.fillRect(x - padding, y - padding, boxWidth, boxHeight);
                ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
                ctx.lineWidth = 1;
                ctx.strokeRect(x - padding, y - padding, boxWidth, boxHeight);

                ctx.fillStyle = '#64748B';
                ctx.font = `bold ${fontSize1}px Inter, sans-serif`;
                ctx.fillText(line1, x, y);

                ctx.font = `italic ${fontSize2}px Inter, sans-serif`;
                ctx.fillStyle = '#94a3b8';
                ctx.fillText(line2, x, y + lineHeight);

                ctx.restore();
            }
        }]
    });

    // Resize handler
    let resizeTimeout;
    function handleResize() {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
            if (chart) {
                const canvasEl = document.getElementById('investmentChart');
                if (canvasEl) {
                    if (window.innerWidth < 768) {
                        canvasEl.style.height = window.matchMedia('(orientation: landscape)').matches ? '250px' : '300px';
                    } else {
                        canvasEl.style.height = '400px';
                    }
                }
                chart.options.plugins.legend.labels.padding = window.innerWidth < 768 ? 12 : 20;
                chart.options.plugins.legend.labels.font.size = window.innerWidth < 768 ? 11 : 13;
                chart.options.plugins.legend.labels.boxWidth = window.innerWidth < 768 ? 6 : 8;
                chart.options.plugins.legend.labels.boxHeight = window.innerWidth < 768 ? 6 : 8;
                chart.resize();
                chart.update();
            }
        }, 100);
    }

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    // Theme change listener
    const themeObserver = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
            if (mutation.attributeName === 'data-theme') {
                const newTheme = document.documentElement.getAttribute('data-theme') || 'light';
                const colors = getChartColors(newTheme);
                chart.options.scales.x.title.color = colors.title;
                chart.options.scales.x.ticks.color = colors.text;
                chart.options.scales.x.grid.color = colors.grid;
                chart.options.scales.y.title.color = colors.title;
                chart.options.scales.y.ticks.color = colors.text;
                chart.options.scales.y.grid.color = colors.grid;
                chart.options.plugins.legend.labels.color = colors.text;
                chart.options.plugins.tooltip.backgroundColor = colors.tooltipBg;
                chart.options.plugins.tooltip.titleColor = colors.tooltipTitle;
                chart.options.plugins.tooltip.bodyColor = colors.tooltipBody;
                chart.options.plugins.tooltip.borderColor = colors.tooltipBorder;
                chart.update();
            }
        });
    });
    themeObserver.observe(document.documentElement, { attributes: true });

    console.log('✅ Investment chart initialized');
}
